import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAbonnement } from '../context/AbonnementContext';
import { useNavigate, useLocation } from 'react-router-dom';
import RappelNotifications from '../components/RappelNotifications';
import NavbarAuth from '../components/NavbarAuth';
import api from '../services/api';
import blog1 from '../blog1.png';
import PAYS from '../utils/pays';


const MOTIFS_FIN = [
    'Nos attentes ne correspondaient pas',
    'Différence de vision du mariage',
    'Différence de pratique religieuse',
    'Distance géographique',
    'Situation familiale incompatible',
    'Je ne me sens pas prêt(e) actuellement',
    'L\'échange n\'a pas abouti',
    'Autre',
];

const PER_PAGE = 8;

const LANGUES = [
    'Français', 'Arabe', 'Malgache', 'Anglais', 'Wolof', 'Bambara',
    'Comorien', 'Berbère', 'Turc', 'Espagnol', 'Autre'
];

const ETHNIES = [
    'Arabe', 'Berbère', 'Africaine subsaharienne', 'Malgache', 'Comorienne',
    'Turque', 'Kurde', 'Persane', 'Sud-asiatique (Inde/Pakistan)',
    'Asiatique de l\'Est', 'Européenne', 'Métisse', 'Autre'
];

const EDUCATION = ['Baccalauréat', 'Licence', 'Master', 'Doctorat', 'Autre'];

// ═══ Dictionnaires pour afficher des libellés lisibles (identique à Profile.js) ═══
const LABELS = {
    marital_status: { celibataire: 'Célibataire', marie: 'Marié(e)', divorce: 'Divorcé(e)', veuf: 'Veuf/Veuve' },
    pratique_religieuse: { peu_pratiquant: 'Peu pratiquant(e)', pratiquant: 'Pratiquant(e)', tres_pratiquant: 'Très pratiquant(e)' },
    priere: { '5_fois': 'Les 5 prières', parfois: 'Parfois', rarement: 'Rarement', jamais: 'Pas encore' },
    porte_voile: { hijab: 'Hijab', khimar: 'Khimar', jilbab: 'Jilbab', abaya: 'Abaya', niqab: 'Niqab', sitar: 'Sitar', pudique_sans_voile: 'Vêtements pudiques', non: 'Ne porte pas encore' },
    barbe: { oui: 'Oui', non: 'Non' },
    ecole_pensee: { hanafi: 'Hanafi', maliki: 'Maliki', shafii: 'Shafi\'i', hanbali: 'Hanbali', sans: 'Sans école particulière' },
    converti: { oui: 'Oui', non: 'Non, de naissance' },
};
const lisible = (champ, valeur) => (LABELS[champ] && LABELS[champ][valeur]) || valeur;

const calculerAge = (dateStr) => {
    if (!dateStr) return '';
    const naissance = new Date(dateStr);
    const today = new Date();
    let age = today.getFullYear() - naissance.getFullYear();
    const m = today.getMonth() - naissance.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < naissance.getDate())) age--;
    return age;
};

// ═══ Hook responsive (identique à Profile / Messages) ═══
const useResponsiveLocal = () => {
    const getState = () => {
        const w = window.innerWidth;
        return { isSmall: w <= 480, isMobile: w <= 768, isTablet: w > 768 && w <= 968 };
    };
    const [state, setState] = useState(getState);
    useEffect(() => {
        const handleResize = () => setState(getState());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return state;
};

// ═══ Icônes SVG monochromes (identiques à Profile / Messages) ═══
const GOLD = '#c0306a';
const Icon = ({ path, size = 18, color = GOLD }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{path}</svg>
);

const icons = {
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
    users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    search: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
    pin: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>,
    globe: <><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
    home: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
    heart: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />,
    mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,6 12,13 2,6" /></>,
    ruler: <><path d="M21.3 8.7l-5.6-5.6a1 1 0 0 0-1.4 0L2.7 14.3a1 1 0 0 0 0 1.4l5.6 5.6a1 1 0 0 0 1.4 0L21.3 10.1a1 1 0 0 0 0-1.4z" /></>,
    sliders: <><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></>,
    close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
    check: <polyline points="20 6 9 17 4 12" />,
    ban: <><circle cx="12" cy="12" r="10" /><line x1="4.9" y1="4.9" x2="19.1" y2="19.1" /></>,
    clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
    send: <><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>,
    refresh: <><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></>,
    venus: <><circle cx="12" cy="9" r="5" /><line x1="12" y1="14" x2="12" y2="22" /><line x1="9" y1="19" x2="15" y2="19" /></>,
    mars: <><circle cx="10" cy="14" r="5" /><line x1="14" y1="10" x2="21" y2="3" /><polyline points="15 3 21 3 21 9" /></>,
    eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
    edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>,
    briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>,
    cap: <><path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.5 3 6 3s6-2 6-3v-5" /></>,
    star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
    book: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>,
    mosque: <><path d="M4 22V11a8 8 0 0 1 16 0v11" /><path d="M12 3c0 1.5 2 2 2 4" /><line x1="2" y1="22" x2="22" y2="22" /><path d="M9 22v-4a3 3 0 0 1 6 0v4" /></>,
    baby: <><path d="M9 12h.01M15 12h.01M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" /><path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1" /></>,
    bookmark: <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />,
    spark: <><path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5z" /></>,
    bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
    crown: <path d="M2 18h20l-2-9-5 4-3-6-3 6-5-4-2 9z" />,
};

const Dashboard = () => {


    const [finTarget, setFinTarget] = useState(null);      // relation à terminer
    const [finMotif, setFinMotif] = useState('');
    const [finCommentaire, setFinCommentaire] = useState('');
    const [finLoading, setFinLoading] = useState(false);

    const { user } = useAuth();
    const { abonnement, requis, actif } = useAbonnement();
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [requests, setRequests] = useState([]);
    const [occupes, setOccupes] = useState([]); // membres déjà en relation
    const [loading, setLoading] = useState(true);
    const [myPhoto, setMyPhoto] = useState(null);
    const [moderation, setModeration] = useState(null);
    // Panneau latéral
    const [showFilters, setShowFilters] = useState(false);

    // Filtres
    const [search, setSearch] = useState('');
    const [filterGender, setFilterGender] = useState('');
    const [filterAgeMin, setFilterAgeMin] = useState('');
    const [filterAgeMax, setFilterAgeMax] = useState('');
    const [filterCity, setFilterCity] = useState('');
    const [filterPaysOrigine, setFilterPaysOrigine] = useState('');
    const [filterPaysResidence, setFilterPaysResidence] = useState('');
    const [filterLangues, setFilterLangues] = useState('');
    const [filterTailleMin, setFilterTailleMin] = useState('');
    const [filterTailleMax, setFilterTailleMax] = useState('');
    const [filterPoidsMin, setFilterPoidsMin] = useState('');
    const [filterPoidsMax, setFilterPoidsMax] = useState('');
    // Nouveaux filtres (données de la fiche de présentation)
    const [filterMarital, setFilterMarital] = useState('');
    const [filterEthnie, setFilterEthnie] = useState('');
    const [filterEnfants, setFilterEnfants] = useState('');
    const [filterEnfantSouhaite, setFilterEnfantSouhaite] = useState('');
    const [filterPratique, setFilterPratique] = useState('');
    const [filterPriere, setFilterPriere] = useState('');
    const [filterEcole, setFilterEcole] = useState('');
    const [filterConverti, setFilterConverti] = useState('');
    const [filterVoile, setFilterVoile] = useState('');
    const [filterBarbe, setFilterBarbe] = useState('');
    const [filterEducation, setFilterEducation] = useState('');
    const [filterProfession, setFilterProfession] = useState('');

    const [appliedSearch, setAppliedSearch] = useState('');
    const [appliedGender, setAppliedGender] = useState('');
    const [appliedAgeMin, setAppliedAgeMin] = useState('');
    const [appliedAgeMax, setAppliedAgeMax] = useState('');
    const [appliedCity, setAppliedCity] = useState('');
    const [appliedPaysOrigine, setAppliedPaysOrigine] = useState('');
    const [appliedPaysResidence, setAppliedPaysResidence] = useState('');
    const [appliedLangues, setAppliedLangues] = useState('');
    const [appliedTailleMin, setAppliedTailleMin] = useState('');
    const [appliedTailleMax, setAppliedTailleMax] = useState('');
    const [appliedPoidsMin, setAppliedPoidsMin] = useState('');
    const [appliedPoidsMax, setAppliedPoidsMax] = useState('');
    const [appliedMarital, setAppliedMarital] = useState('');
    const [appliedEthnie, setAppliedEthnie] = useState('');
    const [appliedEnfants, setAppliedEnfants] = useState('');
    const [appliedEnfantSouhaite, setAppliedEnfantSouhaite] = useState('');
    const [appliedPratique, setAppliedPratique] = useState('');
    const [appliedPriere, setAppliedPriere] = useState('');
    const [appliedEcole, setAppliedEcole] = useState('');
    const [appliedConverti, setAppliedConverti] = useState('');
    const [appliedVoile, setAppliedVoile] = useState('');
    const [appliedBarbe, setAppliedBarbe] = useState('');
    const [appliedEducation, setAppliedEducation] = useState('');
    const [appliedProfession, setAppliedProfession] = useState('');
    const [page, setPage] = useState(1);
    const [showEndModal, setShowEndModal] = useState(false);
    const [ending, setEnding] = useState(false);

    // ═══ Voir le profil ═══
    const [viewProfile, setViewProfile] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);

    // ═══ Intérêts & Favoris ═══
    const [interestsSent, setInterestsSent] = useState([]); // [{ id, receiver_id, message }]
    const [favoris, setFavoris] = useState([]); // [{ favori_row_id, favori_id }]
    const [interestLoading, setInterestLoading] = useState(false);
    const [favoriLoading, setFavoriLoading] = useState(false);
    const [showAbonnementModal, setShowAbonnementModal] = useState(false);

    // ═══ Section "Intérêts reçus & Mes favoris" ═══
    const [interestsReceived, setInterestsReceived] = useState([]);
    const [showActivite, setShowActivite] = useState(false);
    const [activiteTab, setActiviteTab] = useState('recus'); // 'recus' | 'envoyes' | 'favoris'

    // Ouverture directe depuis une notification : /dashboard?vue=interets
    // On nettoie l'URL juste apres, sinon un rafraichissement de page
    // rouvrirait le tiroir a chaque fois.
    const location = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const vue = params.get('vue');
        const profil = params.get('profil');

        if (vue === 'interets') {
            setShowActivite(true);
            setActiviteTab('recus');
            navigate('/dashboard', { replace: true });
        } else if (profil) {
            // La fiche se remplit en deux temps : handleViewProfile affiche
            // d'abord ce qu'il a, puis complete avec l'appel au serveur.
            handleViewProfile({ user_id: Number(profil) });
            navigate('/dashboard', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);
    const [interestsSentDetails, setInterestsSentDetails] = useState([]);

    const { isSmall, isMobile, isTablet } = useResponsiveLocal();
    const compact = isMobile || isTablet;

    useEffect(() => {
        fetchProfiles();
        fetchRequests();
        fetchMyProfile();
        fetchInterestsSent();
        fetchInterestsReceived();
        fetchFavoris();
        fetchModeration();
    }, []);
    const fetchModeration = async () => {
        try {
            const response = await api.get('/moderation');
            setModeration(response.data);
        } catch (err) {
            console.error(err);
        }
    };
    const fetchProfiles = async () => {
        try {
            const response = await api.get('/profiles');
            setProfiles(response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchRequests = async () => {
        try {
            const response = await api.get('/requests/status');
            // La route renvoie { demandes, occupes }.
            // Compatibilité : un tableau simple reste accepté.
            if (Array.isArray(response.data)) {
                setRequests(response.data);
                setOccupes([]);
            } else {
                setRequests(response.data.demandes || []);
                setOccupes(response.data.occupes || []);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchMyProfile = async () => {
        try {
            const response = await api.get(`/profiles/${user.id}`);
            if (response.data) {
                setMyPhoto(response.data.photo_url);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const getRequestState = (profileUserId) => {
        const matches = requests.filter(
            r => (r.sender_id === user?.id && r.receiver_id === profileUserId) ||
                (r.sender_id === profileUserId && r.receiver_id === user?.id)
        );

        // Aucune demande entre nous : ce membre est-il déjà pris ?
        if (matches.length === 0) {
            if (occupes.includes(profileUserId)) return { type: 'occupe' };
            return { type: 'none' };
        }

        // Plusieurs lignes peuvent exister entre deux membres (une par sens).
        // On garde la plus significative : relation active > demande en cours > refusée > terminée.
        const priorite = { accepted: 1, pending: 2, rejected: 3, expired: 4, ended: 5 };
        const req = [...matches].sort(
            (a, b) => (priorite[a.status] || 9) - (priorite[b.status] || 9)
        )[0];

        if (req.status === 'accepted') return { type: 'accepted' };
        if (req.status === 'rejected') {
            const rejectedDate = new Date(req.created_at);
            const fifteenDaysLater = new Date(rejectedDate.getTime() + 15 * 24 * 60 * 60 * 1000);
            const peutRenvoyer = new Date() >= fifteenDaysLater;
            // Même autorisé à renvoyer, le membre peut être pris entre-temps
            if (peutRenvoyer && occupes.includes(profileUserId)) return { type: 'occupe' };
            return { type: 'rejected', canResend: peutRenvoyer };
        }
        if (req.status === 'expired' || req.status === 'ended') {
            // La demande est close : le membre est-il de nouveau disponible ?
            if (occupes.includes(profileUserId)) return { type: 'occupe' };
            return { type: req.status };
        }
        if (req.sender_id === user?.id) return { type: 'sent', id: req.id };
        return { type: 'received', id: req.id };
    };

    const handleSendRequest = async (profileUserId) => {
        try {
            await api.post('/requests', { receiver_id: profileUserId });
            fetchRequests();
        } catch (err) {
            alert(err.response?.data?.message || 'Erreur lors de l\'envoi');
            // Le membre vient d'être pris entre l'affichage et le clic :
            // on recharge pour que la carte reflète la réalité.
            if (err.response?.data?.destinataire_occupe) fetchRequests();
        }
    };

    const handleAccept = async (requestId) => {
        try {
            await api.put(`/requests/${requestId}/accept`);
            fetchRequests();
        } catch (err) {
            alert(err.response?.data?.message || 'Erreur');
        }
    };

    const handleReject = async (requestId) => {
        try {
            await api.put(`/requests/${requestId}/reject`);
            fetchRequests();
        } catch (err) {
            console.error(err);
        }
    };



    // Un motif est obligatoire cote serveur : soit un motif predefini,
    // soit un commentaire ecrit. Le bouton reste inactif tant qu'il n'y a
    // ni l'un ni l'autre.
    const motifRenseigne = Boolean(finMotif || finCommentaire.trim());

    const handleEndRelation = async () => {
        setEnding(true);
        try {
            const rel = requests.find(r => r.status === 'accepted' &&
                (r.sender_id === partnerId || r.receiver_id === partnerId));
            if (!rel) return;

            const motifFinal = finCommentaire.trim()
                ? (finMotif ? `${finMotif} — ${finCommentaire.trim()}` : finCommentaire.trim())
                : finMotif;

            if (!motifFinal.trim()) {
                setEnding(false);
                return;
            }

            await api.put(`/requests/${rel.id}/end`, { motif: motifFinal.trim() });

            setShowEndModal(false);
            setFinMotif('');
            setFinCommentaire('');
            fetchRequests();
        } catch (err) {
            alert(err.response?.data?.message || 'Erreur');
        } finally {
            setEnding(false);
        }
    };

    // ═══ Voir le profil : ouverture + chargement du détail ═══
    const handleViewProfile = async (profile) => {
        // Bloqué uniquement si l'abonnement est requis et absent
        if (!actif && user?.role !== 'admin') {
            setShowAbonnementModal(true);
            return;
        }

        setShowProfileModal(true);
        setProfileLoading(true);
        setViewProfile(profile); // affichage immédiat avec ce qu'on a déjà (liste)
        try {
            const response = await api.get(`/profiles/${profile.user_id}`);
            if (response.data) {
                setViewProfile({ ...profile, ...response.data });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setProfileLoading(false);
        }
    };

    const closeProfileModal = () => {
        setShowProfileModal(false);
        setViewProfile(null);
    };

    // ═══ Intérêts ═══
    const fetchInterestsSent = async () => {
        try {
            const response = await api.get('/interests/sent');
            setInterestsSent(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchInterestsReceived = async () => {
        try {
            const response = await api.get('/interests/received');
            setInterestsReceived(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    // Intérêts envoyés avec le profil du destinataire (pour le drawer)
    const fetchInterestsSentDetails = async () => {
        try {
            const response = await api.get('/interests/sent-details');
            setInterestsSentDetails(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    // Envoie l'intérêt, ou le retire s'il a déjà été envoyé.
    // Signal simple : aucun message d'accompagnement.
    const handleToggleInterest = async (receiverId) => {
        if (!receiverId) return;
        const dejaEnvoye = interestsSent.some(i => i.receiver_id === receiverId);
        setInterestLoading(true);
        try {
            if (dejaEnvoye) {
                await api.delete(`/interests/${receiverId}`);
            } else {
                await api.post('/interests', { receiver_id: receiverId });
            }
            fetchInterestsSent();
        } catch (err) {
            alert(err.response?.data?.message || 'Erreur lors de l\'envoi de l\'intérêt');
        } finally {
            setInterestLoading(false);
        }
    };

    // ═══ Favoris ═══
    const fetchFavoris = async () => {
        try {
            const response = await api.get('/favoris');
            setFavoris(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleToggleFavori = async (profileUserId) => {
        setFavoriLoading(true);
        const existing = favoris.find(f => f.favori_id === profileUserId);
        try {
            if (existing) {
                await api.delete(`/favoris/${profileUserId}`);
            } else {
                await api.post('/favoris', { favori_id: profileUserId });
            }
            fetchFavoris();
        } catch (err) {
            alert(err.response?.data?.message || 'Erreur lors de la mise à jour des favoris');
        } finally {
            setFavoriLoading(false);
        }
    };

    const applyFilters = () => {
        setAppliedSearch(search);
        setAppliedGender(filterGender);
        setAppliedAgeMin(filterAgeMin);
        setAppliedAgeMax(filterAgeMax);
        setAppliedCity(filterCity);
        setAppliedPaysOrigine(filterPaysOrigine);
        setAppliedPaysResidence(filterPaysResidence);
        setAppliedLangues(filterLangues);
        setAppliedTailleMin(filterTailleMin);
        setAppliedTailleMax(filterTailleMax);
        setAppliedPoidsMin(filterPoidsMin);
        setAppliedPoidsMax(filterPoidsMax);
        setAppliedMarital(filterMarital);
        setAppliedEthnie(filterEthnie);
        setAppliedEnfants(filterEnfants);
        setAppliedEnfantSouhaite(filterEnfantSouhaite);
        setAppliedPratique(filterPratique);
        setAppliedPriere(filterPriere);
        setAppliedEcole(filterEcole);
        setAppliedConverti(filterConverti);
        setAppliedVoile(filterVoile);
        setAppliedBarbe(filterBarbe);
        setAppliedEducation(filterEducation);
        setAppliedProfession(filterProfession);
        setPage(1);
        setShowFilters(false);
    };

    const resetFilters = () => {
        setSearch('');
        setFilterGender('');
        setFilterAgeMin('');
        setFilterAgeMax('');
        setFilterCity('');
        setFilterPaysOrigine('');
        setFilterPaysResidence('');
        setFilterLangues('');
        setFilterTailleMin('');
        setFilterTailleMax('');
        setFilterPoidsMin('');
        setFilterPoidsMax('');
        setFilterMarital('');
        setFilterEthnie('');
        setFilterEnfants('');
        setFilterEnfantSouhaite('');
        setFilterPratique('');
        setFilterPriere('');
        setFilterEcole('');
        setFilterConverti('');
        setFilterVoile('');
        setFilterBarbe('');
        setFilterEducation('');
        setFilterProfession('');
        setAppliedSearch('');
        setAppliedGender('');
        setAppliedAgeMin('');
        setAppliedAgeMax('');
        setAppliedCity('');
        setAppliedPaysOrigine('');
        setAppliedPaysResidence('');
        setAppliedLangues('');
        setAppliedTailleMin('');
        setAppliedTailleMax('');
        setAppliedPoidsMin('');
        setAppliedPoidsMax('');
        setAppliedMarital('');
        setAppliedEthnie('');
        setAppliedEnfants('');
        setAppliedEnfantSouhaite('');
        setAppliedPratique('');
        setAppliedPriere('');
        setAppliedEcole('');
        setAppliedConverti('');
        setAppliedVoile('');
        setAppliedBarbe('');
        setAppliedEducation('');
        setAppliedProfession('');
        setPage(1);
    };

    // ═══ Relation active (demande acceptée) ═══
    const acceptedRequest = requests.find(r => r.status === 'accepted');
    const partnerId = acceptedRequest
        ? (acceptedRequest.sender_id === user?.id ? acceptedRequest.receiver_id : acceptedRequest.sender_id)
        : null;
    // Prénom du partenaire, cherché dans plusieurs sources possibles selon
    // ce que renvoie l'API (profil chargé, ou champs de la demande acceptée).
    const partnerName = partnerId
        ? (
            profiles.find(p => p.user_id === partnerId)?.first_name
            || acceptedRequest?.sender_first_name
            || acceptedRequest?.receiver_first_name
            || acceptedRequest?.first_name
            || 'ce membre'
        )
        : 'ce membre';
    // Filtrage
    const filtered = profiles.filter(p => {
        // Si une relation acceptée existe → afficher UNIQUEMENT ce membre
        if (partnerId) {
            return p.user_id === partnerId;
        }

        // Le numéro est inclus dans le texte cherché : on peut donc saisir
        // « 223389 » aussi bien que « #223389 », le croisillon étant retiré
        // de la saisie avant comparaison.
        const txt = `${p.first_name || ''} ${p.city || ''} ${p.numero_membre || ''}`.toLowerCase();
        if (appliedSearch && !txt.includes(appliedSearch.toLowerCase().replace('#', '').trim())) return false;
        if (appliedGender && p.gender !== appliedGender) return false;
        if (appliedCity && p.city !== appliedCity) return false;
        if (appliedAgeMin && p.age < Number(appliedAgeMin)) return false;
        if (appliedAgeMax && p.age > Number(appliedAgeMax)) return false;
        if (appliedPaysOrigine && p.pays_origine !== appliedPaysOrigine) return false;
        if (appliedPaysResidence && p.pays_residence !== appliedPaysResidence) return false;
        if (appliedLangues && p.langues !== appliedLangues) return false;
        if (appliedTailleMin && p.taille < Number(appliedTailleMin)) return false;
        if (appliedTailleMax && p.taille > Number(appliedTailleMax)) return false;
        if (appliedPoidsMin && p.poids < Number(appliedPoidsMin)) return false;
        if (appliedPoidsMax && p.poids > Number(appliedPoidsMax)) return false;
        if (appliedMarital && p.marital_status !== appliedMarital) return false;
        if (appliedEthnie && p.ethnie !== appliedEthnie) return false;
        if (appliedEnfants !== '' && Number(p.children ?? -1) !== Number(appliedEnfants)) return false;
        if (appliedEnfantSouhaite !== '' && Number(p.enfant_souhaite ?? -1) !== Number(appliedEnfantSouhaite)) return false;
        if (appliedPratique && p.pratique_religieuse !== appliedPratique) return false;
        if (appliedPriere && p.priere !== appliedPriere) return false;
        if (appliedEcole && p.ecole_pensee !== appliedEcole) return false;
        if (appliedConverti && p.converti !== appliedConverti) return false;
        if (appliedVoile && p.porte_voile !== appliedVoile) return false;
        if (appliedBarbe && p.barbe !== appliedBarbe) return false;
        if (appliedEducation && p.education !== appliedEducation) return false;
        if (appliedProfession && !(p.profession || '').toLowerCase().includes(appliedProfession.toLowerCase())) return false;
        return true;
    });

    const totalPages = Math.ceil(filtered.length / PER_PAGE) || 1;
    const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    const cities = [...new Set(profiles.map(p => p.city).filter(Boolean))];

    // ═══ État intérêt / favori pour le profil actuellement affiché dans la modale ═══
    const currentInterest = viewProfile ? interestsSent.find(i => i.receiver_id === viewProfile.user_id) : null;
    const isInterestSent = !!currentInterest;
    const isFavori = viewProfile ? favoris.some(f => f.favori_id === viewProfile.user_id) : false;

    // ═══ Accès à la fiche détaillée ═══
    // Sans abonnement, la fiche complète n'est pas accessible.
    // Les boutons Intérêt / Favoris restent disponibles depuis les cartes de l'annuaire.
    const ficheBloquee = !actif && user?.role !== 'admin';

    // Total affiché dans le badge du bouton Intérêts & Favoris
    const activiteCount = interestsReceived.length + favoris.length;

    const activeFiltersCount = [
        appliedSearch, appliedGender, appliedAgeMin, appliedAgeMax, appliedCity,
        appliedPaysOrigine, appliedPaysResidence, appliedLangues,
        appliedTailleMin, appliedTailleMax, appliedPoidsMin, appliedPoidsMax,
        appliedMarital, appliedEthnie, appliedEnfants, appliedEnfantSouhaite,
        appliedPratique, appliedPriere, appliedEcole, appliedConverti,
        appliedVoile, appliedBarbe, appliedEducation, appliedProfession
    ].filter(v => v !== '' && v !== null && v !== undefined).length;

    const renderButton = (profile) => {
        const state = getRequestState(profile.user_id);
        switch (state.type) {
            case 'accepted':
                return (
                    <button onClick={() => navigate(`/messages?to=${profile.user_id}`)} style={styles.cardBtn}>
                        <Icon path={icons.mail} size={13} color="#f3e0c7" /> Contacter
                    </button>
                );
            case 'sent':
                return (
                    <button disabled style={{ ...styles.cardBtn, ...styles.pendingBtn }}>
                        <Icon path={icons.clock} size={13} color="#fff" /> En attente
                    </button>
                );
            case 'received':
                return (
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => handleAccept(state.id)} style={{ ...styles.cardBtn, ...styles.acceptBtn, flex: 1 }}>
                            <Icon path={icons.check} size={13} color="#fff" /> Accepter
                        </button>
                        <button onClick={() => handleReject(state.id)} style={{ ...styles.cardBtn, ...styles.rejectBtn, flex: 1 }}>
                            <Icon path={icons.close} size={13} color="#fff" /> Refuser
                        </button>
                    </div>
                );
            case 'rejected':
                return state.canResend
                    ? (
                        <button onClick={() => handleSendRequest(profile.user_id)} style={{ ...styles.cardBtn, ...styles.expiredBtn }}>
                            <Icon path={icons.send} size={13} color="white" /> Renvoyer
                        </button>
                    )
                    : (
                        <button disabled style={{ ...styles.cardBtn, ...styles.rejectedBtn }}>
                            <Icon path={icons.ban} size={13} color="#9a7d88" /> Refusée
                        </button>
                    );
            case 'occupe':
                return (
                    <button disabled style={{ ...styles.cardBtn, ...styles.occupeBtn }}
                        title="Ce membre est déjà en relation avec une autre personne">
                        <Icon path={icons.heart} size={13} color="#9a7d88" /> Déjà en relation
                    </button>
                );
            case 'expired':
            case 'ended':
            default:
                return (
                    <button onClick={() => handleSendRequest(profile.user_id)} style={styles.cardBtn}>
                        <Icon path={icons.send} size={13} color="#f3e0c7" /> Envoyer une demande
                    </button>
                );
        }
    };

    // ═══ Styles responsives ═══
    const layoutStyle = {
        maxWidth: '1320px',
        margin: '0 auto',
        padding: isSmall ? '12px' : isMobile ? '16px' : '32px',
        boxSizing: 'border-box',
    };

    const cardStyle = {
        ...styles.mainCard,
        padding: isSmall ? '16px' : isMobile ? '20px' : isTablet ? '28px' : '40px',
    };

    const heroStyle = {
        ...styles.hero,
        padding: isSmall ? '28px 16px' : isMobile ? '32px 20px' : '48px 64px',
    };

    // Label réutilisable (identique à Profile)
    const Label = ({ icon, children }) => (
        <label style={styles.label}><span style={styles.labelIcon}><Icon path={icon} size={16} /></span>{children}</label>
    );

    // ═══ Ligne d'information dans les cartes de la fiche profil ═══
    const InfoLigne = ({ label, val }) => (
        <div style={styles.infoLigne}>
            <span style={styles.infoLabel}>{label}</span>
            <span style={styles.infoVal}>{val && String(val).trim() !== '' ? val : 'Non renseigné'}</span>
        </div>
    );

    return (
        <div style={styles.page}>
            <NavbarAuth myPhoto={myPhoto} />

            {/* ═══ HERO ═══ */}
            <div style={heroStyle}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${blog1})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(61,10,38,0.75), rgba(42,5,24,0.55))' }} />
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <p style={styles.breadcrumb}>Accueil › Annuaire des membres</p>
                    <h1 style={{ ...styles.heroTitle, fontSize: isSmall ? '26px' : isMobile ? '32px' : '40px' }}>
                        Annuaire des <span style={{ color: '#E88BB4' }}>membres</span>
                    </h1>
                    <p style={styles.heroText}>
                        Découvrez les membres de la communauté Zawaj &amp; Tawadud.<br />
                        Des hommes et des femmes sérieux, prêts à construire une union bénie incha'Allah.
                    </p>
                </div>
            </div>

            {/* ═══ CONTENU ═══ */}
            <div style={layoutStyle}>
                <main style={cardStyle}>

                    {/* Rappel d'activation des notifications.
                        Se masque tout seul une fois activé ou fermé. */}
                    <RappelNotifications />

                    {moderation && moderation.actif && (
                        <div style={styles.modBanner}>
                            <span style={moderation.ouvert ? styles.modDotOuvert : styles.modDotFerme} />
                            <span>
                                {moderation.ouvert ? (
                                    <>
                                        <strong>Modération ouverte</strong> — vos messages sont vérifiés
                                        puis transmis. Horaires : {String(moderation.heure_debut).padStart(2, '0')}h
                                        à {String(moderation.heure_fin).padStart(2, '0')}h (heure de Paris).
                                    </>
                                ) : (
                                    <>
                                        <strong>Modération fermée</strong> — la modération est assurée
                                        de {String(moderation.heure_debut).padStart(2, '0')}h
                                        à {String(moderation.heure_fin).padStart(2, '0')}h (heure de Paris).
                                        Les messages envoyés en dehors de ces horaires seront transmis
                                        dès l'ouverture.
                                    </>
                                )}
                            </span>
                        </div>
                    )}




                    {/* Bandeau invitation à s'abonner (fiches bloquées) */}
                    {ficheBloquee && (
                        <div style={styles.aboBanner}>
                            <div style={styles.aboBannerText}>
                                <Icon path={icons.crown} size={18} color="#c0306a" />
                                <span>
                                    Vous consultez l'annuaire en accès libre. Un abonnement vous
                                    donnerait accès aux fiches détaillées de chaque membre.
                                </span>
                            </div>
                            <button onClick={() => navigate('/abonnement')} style={styles.aboBannerBtn}>
                                <Icon path={icons.crown} size={14} color="#f3e0c7" /> Voir les tarifs
                            </button>
                        </div>
                    )}

                    {/* Bandeau relation active */}
                    {partnerId && (
                        <div style={styles.relationBanner}>
                            <div style={styles.relationBannerText}>
                                <Icon path={icons.heart} size={18} color="#c0306a" />
                                <span>
                                    Vous êtes actuellement en relation avec <strong>{partnerName}</strong>. L'annuaire est mis en pause le temps de votre échange.
                                    Vous retrouverez les autres profils si cette relation se termine.
                                </span>
                            </div>
                            <button onClick={() => setShowEndModal(true)} style={styles.endRelationBtn}>
                                <Icon path={icons.ban} size={15} color="#ffffff" /> Mettre fin à l'échange
                            </button>
                        </div>
                    )}

                    {/* En-tête : titre + bouton filtrer */}
                    <div style={styles.topBar}>
                        <h2 style={{ ...styles.title, fontSize: isSmall ? '22px' : isMobile ? '25px' : '30px' }}>
                            <span style={styles.titleIcon}>
                                <Icon path={icons.users} size={isSmall ? 20 : 26} color="#4a0028" />
                            </span>
                            Annuaire
                        </h2>
                        <div style={styles.topBarBtns}>
                            <button onClick={() => setShowActivite(true)} style={styles.activiteBtn}>
                                <Icon path={icons.bell} size={16} color="#8b1a4a" />
                                Intérêts &amp; Favoris
                                {activiteCount > 0 && <span style={styles.activiteBadge}>{activiteCount}</span>}
                            </button>
                            {!partnerId && (
                                <button onClick={() => setShowFilters(true)} style={styles.filterBtn}>
                                    <Icon path={icons.sliders} size={16} color="#f3e0c7" />
                                    Rechercher &amp; filtrer
                                    {activeFiltersCount > 0 && <span style={styles.fabBadge}>{activeFiltersCount}</span>}
                                </button>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div style={styles.loading}>Chargement des profils...</div>
                    ) : paginated.length === 0 ? (
                        <div style={styles.empty}>Aucun membre ne correspond à votre recherche.</div>
                    ) : (
                        <>
                            <div style={{ ...styles.grid, gridTemplateColumns: isSmall ? '1fr' : isMobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                                {paginated.map(profile => (
                                    <div key={profile.id} style={styles.card}>
                                        <div style={styles.avatarWrap}>
                                            {profile.photo_url ? (
                                                <img src={profile.photo_url} alt={profile.first_name} style={styles.avatarImg} />
                                            ) : (
                                                <div style={styles.avatarPlaceholder}>
                                                    <Icon path={icons.user} size={36} color="#c0306a" />
                                                </div>
                                            )}
                                            <span style={styles.genderDot}>
                                                <Icon path={profile.gender === 'femme' ? icons.venus : icons.mars} size={12} color="#fff" />
                                            </span>
                                        </div>
                                        <h3 style={styles.cardName}>
                                            <Icon path={profile.gender === 'femme' ? icons.venus : icons.mars} size={13} color="#c0306a" />
                                            {profile.first_name || 'Anonyme'}
                                        </h3>
                                        <p style={styles.cardSub}>{profile.age} ans • {profile.city}</p>
                                        <p style={styles.cardStatus}>
                                            <Icon path={icons.heart} size={10} color="#c0306a" /> En quête d'une union sérieuse
                                        </p>
                                        <div style={{ marginTop: '12px' }}>{renderButton(profile)}</div>

                                        {ficheBloquee ? (
                                            // Sans abonnement : actions directement sur la carte
                                            <div style={styles.cardActionsRow}>
                                                <button
                                                    onClick={() => handleToggleInterest(profile.user_id)}
                                                    disabled={interestLoading}
                                                    style={interestsSent.some(i => i.receiver_id === profile.user_id)
                                                        ? styles.cardMiniBtnActive : styles.cardMiniBtn}
                                                    title={interestsSent.some(i => i.receiver_id === profile.user_id)
                                                        ? 'Retirer mon intérêt' : 'Envoyer un intérêt'}
                                                >
                                                    <Icon path={icons.heart} size={13}
                                                        color={interestsSent.some(i => i.receiver_id === profile.user_id) ? '#4a0028' : '#8b1a4a'} />
                                                </button>
                                                <button
                                                    onClick={() => handleToggleFavori(profile.user_id)}
                                                    disabled={favoriLoading}
                                                    style={favoris.some(f => f.favori_id === profile.user_id)
                                                        ? styles.cardMiniBtnActive : styles.cardMiniBtn}
                                                    title={favoris.some(f => f.favori_id === profile.user_id)
                                                        ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                                >
                                                    <Icon path={icons.star} size={13}
                                                        color={favoris.some(f => f.favori_id === profile.user_id) ? '#4a0028' : '#8b1a4a'} />
                                                </button>
                                                <button
                                                    onClick={() => setShowAbonnementModal(true)}
                                                    style={styles.cardLockBtn}
                                                    title="Abonnement nécessaire"
                                                >
                                                    <Icon path={icons.lock} size={13} color="#9a7d88" /> Profil
                                                </button>
                                            </div>
                                        ) : (
                                            <button onClick={() => handleViewProfile(profile)} style={styles.viewProfileBtn}>
                                                <Icon path={icons.eye} size={13} color="#8b1a4a" /> Voir le profil
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {!partnerId && totalPages > 1 && (
                                <div style={styles.pagination}>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                                        <button key={n} onClick={() => setPage(n)} style={n === page ? styles.pageActive : styles.pageBtn}>
                                            {n}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>

            {/* ═══ OVERLAY ═══ */}
            <div
                onClick={() => setShowFilters(false)}
                style={{
                    ...styles.overlay,
                    opacity: showFilters ? 1 : 0,
                    visibility: showFilters ? 'visible' : 'hidden',
                }}
            />

            {/* ═══ PANNEAU LATÉRAL ═══ */}
            <div style={{
                ...styles.drawer,
                width: isMobile ? '88%' : '380px',
                transform: showFilters ? 'translateX(0)' : 'translateX(110%)',
            }}>
                <div style={styles.drawerHeader}>
                    <h3 style={styles.drawerTitle}>
                        <Icon path={icons.sliders} size={18} color="#f3e0c7" /> Rechercher &amp; filtrer
                    </h3>
                    <button onClick={() => setShowFilters(false)} style={styles.closeBtn}>
                        <Icon path={icons.close} size={16} color="#f3e0c7" />
                    </button>
                </div>

                <div style={styles.drawerBody}>
                    {/* Recherche */}
                    <div style={styles.sideCard}>
                        <h4 style={styles.sideTitle}>Rechercher un membre</h4>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
                                placeholder="Kounya, ville ou ID..."
                                style={styles.input}
                            />
                            <button onClick={applyFilters} style={styles.searchBtn}>
                                <Icon path={icons.search} size={14} color="#f3e0c7" />
                            </button>
                        </div>
                    </div>

                    {/* Filtres */}
                    <div style={styles.sideCard}>
                        <h4 style={styles.sideTitle}>Filtres</h4>

                        <Label icon={icons.users}>Je recherche</Label>
                        <select value={filterGender} onChange={e => setFilterGender(e.target.value)} style={styles.select}>
                            <option value="">Homme ou Femme</option>
                            <option value="homme">Homme</option>
                            <option value="femme">Femme</option>
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.calendar}>Âge</Label>
                        </div>
                        <div style={styles.rangeRow}>
                            <input
                                type="number"
                                value={filterAgeMin}
                                onChange={e => setFilterAgeMin(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
                                placeholder="Min"
                                min="18"
                                max="99"
                                style={{ ...styles.select, width: '50%' }}
                            />
                            <span style={styles.rangeSep}>—</span>
                            <input
                                type="number"
                                value={filterAgeMax}
                                onChange={e => setFilterAgeMax(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
                                placeholder="Max"
                                min="18"
                                max="99"
                                style={{ ...styles.select, width: '50%' }}
                            />
                        </div>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.pin}>Localisation</Label>
                        </div>
                        <select value={filterCity} onChange={e => setFilterCity(e.target.value)} style={styles.select}>
                            <option value="">Toutes les villes</option>
                            {cities.map((c, i) => <option key={i} value={c}>{c}</option>)}
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.globe}>Pays d'origine</Label>
                        </div>
                        <select value={filterPaysOrigine} onChange={e => setFilterPaysOrigine(e.target.value)} style={styles.select}>
                            <option value="">Tous les pays</option>
                            {PAYS.map((p, i) => <option key={i} value={p}>{p}</option>)}
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.home}>Pays de résidence</Label>
                        </div>
                        <select value={filterPaysResidence} onChange={e => setFilterPaysResidence(e.target.value)} style={styles.select}>
                            <option value="">Tous les pays</option>
                            {PAYS.map((p, i) => <option key={i} value={p}>{p}</option>)}
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.mail}>Langues</Label>
                        </div>
                        <select value={filterLangues} onChange={e => setFilterLangues(e.target.value)} style={styles.select}>
                            <option value="">Toutes les langues</option>
                            {LANGUES.map((l, i) => <option key={i} value={l}>{l}</option>)}
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.ruler}>Taille (cm)</Label>
                        </div>
                        <div style={styles.rangeRow}>
                            <input type="number" value={filterTailleMin} onChange={e => setFilterTailleMin(e.target.value)}
                                placeholder="Min" style={{ ...styles.select, width: '50%' }} />
                            <span style={styles.rangeSep}>—</span>
                            <input type="number" value={filterTailleMax} onChange={e => setFilterTailleMax(e.target.value)}
                                placeholder="Max" style={{ ...styles.select, width: '50%' }} />
                        </div>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.ruler}>Poids (kg)</Label>
                        </div>
                        <div style={styles.rangeRow}>
                            <input type="number" value={filterPoidsMin} onChange={e => setFilterPoidsMin(e.target.value)}
                                placeholder="Min" style={{ ...styles.select, width: '50%' }} />
                            <span style={styles.rangeSep}>—</span>
                            <input type="number" value={filterPoidsMax} onChange={e => setFilterPoidsMax(e.target.value)}
                                placeholder="Max" style={{ ...styles.select, width: '50%' }} />
                        </div>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.heart}>Situation matrimoniale</Label>
                        </div>
                        <select value={filterMarital} onChange={e => setFilterMarital(e.target.value)} style={styles.select}>
                            <option value="">Toutes les situations</option>
                            <option value="celibataire">Célibataire</option>
                            <option value="marie">Marié(e)</option>
                            <option value="divorce">Divorcé(e)</option>
                            <option value="veuf">Veuf/Veuve</option>
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.globe}>Ethnie</Label>
                        </div>
                        <select value={filterEthnie} onChange={e => setFilterEthnie(e.target.value)} style={styles.select}>
                            <option value="">Toutes les origines</option>
                            {ETHNIES.map((e, i) => <option key={i} value={e}>{e}</option>)}
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.users}>Nombre d'enfants</Label>
                        </div>
                        <select value={filterEnfants} onChange={e => setFilterEnfants(e.target.value)} style={styles.select}>
                            <option value="">Peu importe</option>
                            <option value="0">Aucun</option>
                            <option value="1">1 enfant</option>
                            <option value="2">2 enfants</option>
                            <option value="3">3 enfants</option>
                            <option value="4">4 enfants ou plus</option>
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.baby}>Enfants souhaités</Label>
                        </div>
                        <select value={filterEnfantSouhaite} onChange={e => setFilterEnfantSouhaite(e.target.value)} style={styles.select}>
                            <option value="">Peu importe</option>
                            <option value="0">Aucun</option>
                            <option value="1">1 enfant</option>
                            <option value="2">2 enfants</option>
                            <option value="3">3 enfants</option>
                            <option value="4">4 enfants ou plus</option>
                        </select>
                    </div>

                    {/* Filtres religieux */}
                    <div style={styles.sideCard}>
                        <h4 style={styles.sideTitle}>Pratique religieuse</h4>

                        <Label icon={icons.mosque}>Niveau de pratique</Label>
                        <select value={filterPratique} onChange={e => setFilterPratique(e.target.value)} style={styles.select}>
                            <option value="">Peu importe</option>
                            <option value="peu_pratiquant">Peu pratiquant(e)</option>
                            <option value="pratiquant">Pratiquant(e)</option>
                            <option value="tres_pratiquant">Très pratiquant(e)</option>
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.star}>Prière</Label>
                        </div>
                        <select value={filterPriere} onChange={e => setFilterPriere(e.target.value)} style={styles.select}>
                            <option value="">Peu importe</option>
                            <option value="5_fois">Les 5 prières</option>
                            <option value="parfois">Parfois</option>
                            <option value="rarement">Rarement</option>
                            <option value="jamais">Pas encore</option>
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.book}>École de pensée</Label>
                        </div>
                        <select value={filterEcole} onChange={e => setFilterEcole(e.target.value)} style={styles.select}>
                            <option value="">Toutes les écoles</option>
                            <option value="hanafi">Hanafi</option>
                            <option value="maliki">Maliki</option>
                            <option value="shafii">Shafi'i</option>
                            <option value="hanbali">Hanbali</option>
                            <option value="sans">Sans école particulière</option>
                        </select>

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.heart}>Converti(e) à l'Islam</Label>
                        </div>
                        <select value={filterConverti} onChange={e => setFilterConverti(e.target.value)} style={styles.select}>
                            <option value="">Peu importe</option>
                            <option value="oui">Oui</option>
                            <option value="non">Non, de naissance</option>
                        </select>

                        {/* La tenue ne concerne que les profils féminins */}
                        {user?.gender === 'homme' && (
                            <>
                                <div style={{ marginTop: '16px' }}>
                                    <Label icon={icons.star}>Tenue vestimentaire</Label>
                                </div>
                                <select value={filterVoile} onChange={e => setFilterVoile(e.target.value)} style={styles.select}>
                                    <option value="">Peu importe</option>
                                    <option value="hijab">Hijab</option>
                                    <option value="khimar">Khimar</option>
                                    <option value="jilbab">Jilbab</option>
                                    <option value="abaya">Abaya</option>
                                    <option value="niqab">Niqab</option>
                                    <option value="sitar">Sitar / Gants + Niqab</option>
                                    <option value="pudique_sans_voile">Vêtements pudiques sans voile</option>
                                    <option value="non">Ne porte pas encore le voile</option>
                                </select>
                            </>
                        )}

                        {/* La barbe ne concerne que les profils masculins */}
                        {user?.gender === 'femme' && (
                            <>
                                <div style={{ marginTop: '16px' }}>
                                    <Label icon={icons.star}>Barbe</Label>
                                </div>
                                <select value={filterBarbe} onChange={e => setFilterBarbe(e.target.value)} style={styles.select}>
                                    <option value="">Peu importe</option>
                                    <option value="oui">Oui</option>
                                    <option value="non">Non</option>
                                </select>
                            </>
                        )}
                    </div>

                    {/* Filtres profession */}
                    <div style={styles.sideCard}>
                        <h4 style={styles.sideTitle}>Profession &amp; Études</h4>

                        <Label icon={icons.briefcase}>Profession</Label>
                        <input
                            value={filterProfession}
                            onChange={e => setFilterProfession(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
                            placeholder="Ex : enseignant, médecin..."
                            style={styles.select}
                        />

                        <div style={{ marginTop: '16px' }}>
                            <Label icon={icons.cap}>Niveau d'études</Label>
                        </div>
                        <select value={filterEducation} onChange={e => setFilterEducation(e.target.value)} style={styles.select}>
                            <option value="">Tous les niveaux</option>
                            {EDUCATION.map((e, i) => <option key={i} value={e}>{e}</option>)}
                        </select>

                        <button onClick={applyFilters} style={styles.applyBtn}>
                            <Icon path={icons.check} size={16} color="#f3e0c7" /> Appliquer les filtres
                        </button>
                        <button onClick={resetFilters} style={styles.resetBtn}>
                            <Icon path={icons.refresh} size={15} color="#8b1a4a" /> Réinitialiser
                        </button>
                    </div>

                    {/* Membres */}

                </div>
            </div>

            {/* ═══ OVERLAY NOTIFICATIONS ═══ */}
            <div
                onClick={() => setShowActivite(false)}
                style={{
                    ...styles.overlay,
                    opacity: showActivite ? 1 : 0,
                    visibility: showActivite ? 'visible' : 'hidden',
                }}
            />

            {/* ═══ PANNEAU LATÉRAL NOTIFICATIONS ═══ */}
            <div style={{
                ...styles.drawer,
                width: isMobile ? '92%' : '440px',
                transform: showActivite ? 'translateX(0)' : 'translateX(110%)',
            }}>
                <div style={styles.drawerHeader}>
                    <h3 style={styles.drawerTitle}>
                        <Icon path={icons.bell} size={18} color="#f3e0c7" /> Intérêts &amp; Favoris
                    </h3>
                    <button onClick={() => setShowActivite(false)} style={styles.closeBtn}>
                        <Icon path={icons.close} size={16} color="#f3e0c7" />
                    </button>
                </div>

                <div style={styles.drawerBody}>
                    {/* Onglets */}
                    <div style={styles.activiteTabs}>
                        <button
                            onClick={() => setActiviteTab('recus')}
                            style={activiteTab === 'recus' ? styles.activiteTabActive : styles.activiteTab}
                        >
                            <Icon path={icons.spark} size={14} color={activiteTab === 'recus' ? '#fff' : '#8b1a4a'} />
                            Intérêts reçus ({interestsReceived.length})
                        </button>
                        <button
                            onClick={() => { setActiviteTab('envoyes'); fetchInterestsSentDetails(); }}
                            style={activiteTab === 'envoyes' ? styles.activiteTabActive : styles.activiteTab}
                        >
                            <Icon path={icons.heart} size={14} color={activiteTab === 'envoyes' ? '#fff' : '#8b1a4a'} />
                            Intérêts envoyés ({interestsSent.length})
                        </button>
                        <button
                            onClick={() => setActiviteTab('favoris')}
                            style={activiteTab === 'favoris' ? styles.activiteTabActive : styles.activiteTab}
                        >
                            <Icon path={icons.bookmark} size={14} color={activiteTab === 'favoris' ? '#fff' : '#8b1a4a'} />
                            Mes favoris ({favoris.length})
                        </button>
                    </div>

                    {/* Intérêts reçus */}
                    {activiteTab === 'recus' && (
                        interestsReceived.length === 0 ? (
                            <div style={styles.activiteEmpty}>
                                Personne ne vous a encore envoyé d'intérêt.
                            </div>
                        ) : (
                            <div style={styles.activiteList}>
                                {interestsReceived.map(it => (
                                    <div key={it.interest_id} style={styles.activiteItem}>
                                        <div style={styles.activiteTop}>
                                            <div style={styles.activiteAvatar}>
                                                {it.photo_url
                                                    ? <img src={it.photo_url} alt="" style={styles.avatarImg} />
                                                    : <Icon path={icons.user} size={20} color="#c0306a" />}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={styles.activiteName}>
                                                    <Icon path={it.gender === 'femme' ? icons.venus : icons.mars} size={12} color="#c0306a" />
                                                    {it.first_name || 'Anonyme'}
                                                </div>
                                                <span style={styles.activiteMeta}>
                                                    {it.age ? `${it.age} ans` : ''}{it.city ? ` • ${it.city}` : ''}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={styles.activiteFooter}>
                                            <span style={styles.activiteDate}>
                                                {new Date(it.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    const p = profiles.find(pr => pr.user_id === it.user_id);
                                                    setShowActivite(false);
                                                    handleViewProfile(p || { user_id: it.user_id, first_name: it.first_name, age: it.age, city: it.city, photo_url: it.photo_url, gender: it.gender });
                                                }}
                                                style={styles.activiteViewBtn}
                                            >
                                                <Icon path={icons.eye} size={13} color="#8b1a4a" /> Voir le profil
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {/* Intérêts envoyés */}
                    {activiteTab === 'envoyes' && (
                        interestsSentDetails.length === 0 ? (
                            <div style={styles.activiteEmpty}>
                                Vous n'avez encore manifesté d'intérêt pour personne.
                            </div>
                        ) : (
                            <div style={styles.activiteList}>
                                {interestsSentDetails.map(it => (
                                    <div key={it.interest_id} style={styles.activiteItem}>
                                        <div style={styles.activiteTop}>
                                            <div style={styles.activiteAvatar}>
                                                {it.photo_url
                                                    ? <img src={it.photo_url} alt="" style={styles.avatarImg} />
                                                    : <Icon path={icons.user} size={20} color="#c0306a" />}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={styles.activiteName}>
                                                    <Icon path={it.gender === 'femme' ? icons.venus : icons.mars} size={12} color="#c0306a" />
                                                    {it.first_name || 'Anonyme'}
                                                </div>
                                                <span style={styles.activiteMeta}>
                                                    {it.age ? `${it.age} ans` : ''}{it.city ? ` • ${it.city}` : ''}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={styles.activiteFooter}>
                                            <span style={styles.activiteDate}>
                                                {new Date(it.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </span>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={async () => {
                                                        await handleToggleInterest(it.user_id);
                                                        fetchInterestsSentDetails();
                                                    }}
                                                    disabled={interestLoading}
                                                    style={styles.activiteRetirerBtn}
                                                    title="Retirer mon intérêt"
                                                >
                                                    <Icon path={icons.close} size={13} color="#9a7d88" /> Retirer
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const p = profiles.find(pr => pr.user_id === it.user_id);
                                                        setShowActivite(false);
                                                        handleViewProfile(p || { user_id: it.user_id, first_name: it.first_name, age: it.age, city: it.city, photo_url: it.photo_url, gender: it.gender });
                                                    }}
                                                    style={styles.activiteViewBtn}
                                                >
                                                    <Icon path={icons.eye} size={13} color="#8b1a4a" /> Voir le profil
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {/* Mes favoris */}
                    {activiteTab === 'favoris' && (
                        favoris.length === 0 ? (
                            <div style={styles.activiteEmpty}>
                                Vous n'avez pas encore de favoris. Ajoutez-en depuis la fiche d'un membre.
                            </div>
                        ) : (
                            <div style={styles.activiteList}>
                                {favoris.map(f => (
                                    <div key={f.favori_row_id} style={styles.activiteItem}>
                                        <div style={styles.activiteTop}>
                                            <div style={styles.activiteAvatar}>
                                                {f.photo_url
                                                    ? <img src={f.photo_url} alt="" style={styles.avatarImg} />
                                                    : <Icon path={icons.user} size={20} color="#c0306a" />}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={styles.activiteName}>
                                                    <Icon path={f.gender === 'femme' ? icons.venus : icons.mars} size={12} color="#c0306a" />
                                                    {f.first_name || 'Anonyme'}
                                                </div>
                                                <span style={styles.activiteMeta}>
                                                    {f.age ? `${f.age} ans` : ''}{f.city ? ` • ${f.city}` : ''}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => handleToggleFavori(f.favori_id)}
                                                disabled={favoriLoading}
                                                style={styles.activiteRemoveBtn}
                                                title="Retirer des favoris"
                                            >
                                                <Icon path={icons.close} size={13} color="#9a7d88" />
                                            </button>
                                        </div>
                                        <div style={styles.activiteFooter}>
                                            <span style={styles.activiteDate}>
                                                Ajouté le {new Date(f.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    const p = profiles.find(pr => pr.user_id === f.favori_id);
                                                    setShowActivite(false);
                                                    handleViewProfile(p || { user_id: f.favori_id, first_name: f.first_name, age: f.age, city: f.city, photo_url: f.photo_url, gender: f.gender });
                                                }}
                                                style={styles.activiteViewBtn}
                                            >
                                                <Icon path={icons.eye} size={13} color="#8b1a4a" /> Voir le profil
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
            </div>


            {/* ═══ MODALE FIN DE RELATION ═══ */}
            {showEndModal && (
                <div style={styles.modalOverlay} onClick={() => !ending && setShowEndModal(false)}>
                    <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalIcon}>
                            <Icon path={icons.heart} size={38} color="#c0306a" />
                        </div>
                        <h3 style={styles.modalTitle}>Mettre fin à l'échange</h3>
                        <p style={styles.modalText}>
                            Vous retrouverez l'accès à l'annuaire, et cette personne aussi.
                            Cette action est définitive.
                        </p>

                        <p style={styles.modalText}>
                            Merci d'indiquer un motif. Il sera visible par l'autre membre,
                            qui a le droit de savoir pourquoi l'échange s'arrête.
                        </p>

                        <div style={styles.finMotifs}>
                            {MOTIFS_FIN.map(m => (
                                <button
                                    key={m}
                                    onClick={() => setFinMotif(finMotif === m ? '' : m)}
                                    style={finMotif === m ? styles.finChipActive : styles.finChip}
                                >
                                    {m}
                                </button>
                            ))}
                        </div>

                        <textarea
                            value={finCommentaire}
                            onChange={(e) => setFinCommentaire(e.target.value)}
                            placeholder="Commentaire — ou précisez le motif choisi"
                            style={styles.finTextarea}
                            rows={3}
                            maxLength={250}
                        />

                        <div style={styles.modalActions}>
                            <button onClick={() => setShowEndModal(false)} style={styles.modalCancel}>
                                Annuler
                            </button>
                            <button
                                onClick={handleEndRelation}
                                style={motifRenseigne ? styles.modalConfirm : { ...styles.modalConfirm, opacity: 0.45, cursor: 'not-allowed' }}
                                disabled={ending || !motifRenseigne}
                                title={motifRenseigne ? '' : 'Choisissez un motif ou écrivez un commentaire'}
                            >
                                {ending ? 'En cours...' : 'Mettre fin'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ MODALE VOIR LE PROFIL ═══ */}
            {showProfileModal && (
                <div style={styles.modalOverlay} onClick={closeProfileModal}>
                    <div style={styles.profileModalBox} onClick={(e) => e.stopPropagation()}>
                        <button onClick={closeProfileModal} style={styles.profileModalClose}>
                            <Icon path={icons.close} size={16} color="#8b1a4a" />
                        </button>

                        {profileLoading && !viewProfile ? (
                            <div style={{ padding: '40px 0', textAlign: 'center', color: '#9a7d88' }}>
                                Chargement du profil...
                            </div>
                        ) : viewProfile && (
                            <div style={{ ...styles.profileLayout, gridTemplateColumns: compact ? '1fr' : '300px 1fr' }}>

                                {/* ═══ COLONNE GAUCHE : carte photo + actions ═══ */}
                                <div style={styles.profileSideCard}>
                                    <div style={styles.profilePhotoBox}>
                                        {viewProfile.photo_url ? (
                                            <img src={viewProfile.photo_url} alt="profil" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
                                        ) : (
                                            <Icon path={icons.user} size={40} color="#c0306a" />
                                        )}
                                    </div>

                                    <div style={styles.profileSideBody}>
                                        <h3 style={styles.profileSideName}>
                                            {viewProfile.first_name || '—'}
                                            {(viewProfile.date_naissance || viewProfile.age) && (
                                                <span>, {viewProfile.date_naissance ? calculerAge(viewProfile.date_naissance) + ' ans ' : viewProfile.age + ' ans '}</span>
                                            )}
                                        </h3>
                                        <p style={styles.profileSideLoc}>
                                            <Icon path={icons.pin} size={13} color="#9a7d88" />
                                            {[viewProfile.city, viewProfile.pays_residence].filter(Boolean).join(', ') || 'Non renseigné'}
                                        </p>

                                        {/* Numéro de membre : permet de désigner cette personne
                                            sans ambiguïté auprès de l'équipe. Affiché seulement
                                            s'il existe, pour ne pas montrer un tiret inutile. */}
                                        {viewProfile.numero_membre && (
                                            <span
                                                style={styles.profileSideId}
                                                title="Numéro de ce membre, à indiquer si vous nous contactez à son sujet"
                                            >
                                                <Icon path={icons.bookmark} size={12} color="#c0306a" />
                                                ID :<strong style={{ marginLeft: '2px' }}>#{viewProfile.numero_membre}</strong>
                                            </span>
                                        )}

                                        <button
                                            onClick={() => handleToggleInterest(viewProfile.user_id)}
                                            disabled={interestLoading}
                                            style={isInterestSent ? styles.sideInterestBtnActive : styles.sideInterestBtn}
                                        >
                                            <Icon path={icons.heart} size={14} color={isInterestSent ? '#4a0028' : '#f3e0c7'} />
                                            {isInterestSent ? 'Intérêt envoyé' : 'Envoyer un intérêt'}
                                        </button>

                                        <button
                                            onClick={() => handleToggleFavori(viewProfile.user_id)}
                                            disabled={favoriLoading}
                                            style={isFavori ? styles.sideFavoriBtnActive : styles.sideFavoriBtn}
                                        >
                                            <Icon path={icons.star} size={14} color={isFavori ? '#4a0028' : '#8b1a4a'} />
                                            {isFavori ? 'Dans mes favoris' : 'Ajouter aux favoris'}
                                        </button>
                                    </div>
                                </div>

                                {/* ═══ COLONNE DROITE : informations ═══ */}
                                <div style={styles.profileMainCol}>

                                    {/* À propos de moi */}
                                    <div style={styles.infoCard}>
                                        <h4 style={styles.infoCardTitle}>
                                            <Icon path={icons.edit} size={15} color="#c0306a" /> À propos de moi
                                        </h4>
                                        <p style={styles.aboutText}>
                                            {viewProfile.bio || 'Aucune présentation renseignée.'}
                                        </p>
                                    </div>

                                    {/* Grille de cartes d'informations */}
                                    <div style={{ ...styles.infoGrid, gridTemplateColumns: compact ? '1fr' : '1fr 1fr' }}>

                                        <div style={styles.infoCard}>
                                            <h4 style={styles.infoCardTitle}>
                                                <Icon path={icons.mosque} size={15} color="#c0306a" /> Pratique religieuse
                                            </h4>
                                            <InfoLigne label="Niveau" val={lisible('pratique_religieuse', viewProfile.pratique_religieuse)} />
                                            <InfoLigne label="Prière" val={lisible('priere', viewProfile.priere)} />
                                            <InfoLigne label="Madhhab" val={lisible('ecole_pensee', viewProfile.ecole_pensee)} />
                                            <InfoLigne label="Converti(e)" val={lisible('converti', viewProfile.converti)} />
                                            {viewProfile.gender === 'femme' && <InfoLigne label="Voile" val={lisible('porte_voile', viewProfile.porte_voile)} />}
                                            {viewProfile.gender === 'homme' && <InfoLigne label="Barbe" val={lisible('barbe', viewProfile.barbe)} />}
                                        </div>

                                        <div style={styles.infoCard}>
                                            <h4 style={styles.infoCardTitle}>
                                                <Icon path={icons.user} size={15} color="#c0306a" /> Situation personnelle
                                            </h4>
                                            <InfoLigne label="Statut" val={lisible('marital_status', viewProfile.marital_status)} />
                                            <InfoLigne label="Profession" val={viewProfile.profession} />
                                            <InfoLigne label="Études" val={viewProfile.education} />
                                            <InfoLigne label="Origine" val={viewProfile.pays_origine} />
                                            <InfoLigne label="Ethnie" val={viewProfile.ethnie} />
                                        </div>

                                        <div style={styles.infoCard}>
                                            <h4 style={styles.infoCardTitle}>
                                                <Icon path={icons.users} size={15} color="#c0306a" /> Famille &amp; Langues
                                            </h4>
                                            <InfoLigne label="Enfants" val={viewProfile.children !== '' && viewProfile.children !== null && viewProfile.children !== undefined ? String(viewProfile.children) : ''} />
                                            <InfoLigne label="Enfants souhaités" val={viewProfile.enfant_souhaite !== '' && viewProfile.enfant_souhaite !== null && viewProfile.enfant_souhaite !== undefined ? String(viewProfile.enfant_souhaite) : ''} />
                                            <InfoLigne label="Langues" val={viewProfile.langues} />
                                        </div>

                                        <div style={styles.infoCard}>
                                            <h4 style={styles.infoCardTitle}>
                                                <Icon path={icons.ruler} size={15} color="#c0306a" /> Physique
                                            </h4>
                                            <InfoLigne label="Taille" val={viewProfile.taille ? `${viewProfile.taille} cm` : ''} />
                                            <InfoLigne label="Poids" val={viewProfile.poids ? `${viewProfile.poids} kg` : ''} />
                                        </div>
                                    </div>
                                </div>

                                {/* ═══ BARRE D'ACTION EN BAS ═══ */}
                                <div style={styles.profileFooter}>
                                    {renderButton(viewProfile)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═══ MODALE MESSAGE D'INTÉRÊT ═══ */}

            {/* ═══ MODALE INVITATION À S'ABONNER ═══ */}
            {showAbonnementModal && (
                <div style={styles.modalOverlay} onClick={() => setShowAbonnementModal(false)}>
                    <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalIcon}>
                            <Icon path={icons.crown} size={38} color="#c0306a" />
                        </div>
                        <h3 style={styles.modalTitle}>Fiches réservées aux abonnés</h3>
                        <p style={styles.modalText}>
                            La consultation des fiches détaillées nécessite un abonnement.
                            Vous conservez l'accès à l'annuaire, à l'envoi de demandes,
                            aux intérêts et à vos favoris.
                        </p>
                        <div style={styles.modalActions}>
                            <button onClick={() => setShowAbonnementModal(false)} style={styles.modalCancel}>
                                Plus tard
                            </button>
                            <button onClick={() => navigate('/abonnement')} style={styles.modalConfirm}>
                                Voir les tarifs d'abonnement
                            </button>
                        </div>
                    </div>
                </div>
            )}




        </div>




    );
};

const styles = {


    finOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px', boxSizing: 'border-box' },
    finModal: { backgroundColor: '#fff', borderRadius: '18px', padding: '28px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' },
    finTitre: { color: '#c0306a', fontSize: '20px', fontWeight: '800', margin: '0 0 8px' },
    finSub: { color: '#8a9099', fontSize: '13.5px', lineHeight: '1.55', margin: '0 0 20px' },
    finMotifs: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '18px' },
    finChip: { padding: '8px 14px', backgroundColor: '#fff', color: '#c0306a', border: '1.5px solid #f5d0e0', borderRadius: '30px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', textAlign: 'left' },
    finChipActive: { padding: '8px 14px', background: '#c0306a', color: '#fff', border: '1.5px solid #c0306a', borderRadius: '30px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', textAlign: 'left' },
    finTextarea: { width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #f5d0e0', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', outline: 'none', color: '#3a3a3a', marginBottom: '18px' },
    finActions: { display: 'flex', gap: '12px' },
    finCancel: { flex: 1, padding: '12px', backgroundColor: '#fff', color: '#c0306a', border: '1.5px solid #f5d0e0', borderRadius: '10px', fontWeight: '700', fontSize: '14px', cursor: 'pointer' },
    finConfirm: { flex: 2, padding: '12px', backgroundColor: '#c0306a', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '14px', cursor: 'pointer' },





    modBanner: { display: 'flex', alignItems: 'flex-start', gap: '10px', background: 'rgba(60,60,80,0.04)', border: '1px solid #eceef1', borderRadius: '14px', padding: '14px 18px', marginBottom: '20px', color: '#5a4a52', fontSize: '13.5px', lineHeight: '1.6' },
    modDotOuvert: { width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#2c5f2e', flexShrink: 0, marginTop: '6px' },
    modDotFerme: { width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#9a7d88', flexShrink: 0, marginTop: '6px' },


    // ═══ Base (identique à Profile / Messages) ═══
    page: { minHeight: '100vh', width: '100%', boxSizing: 'border-box', overflowX: 'hidden', background: 'linear-gradient(160deg, #fdfdfe 0%, #f5f6f8 50%, #eef0f3 100%)', backgroundAttachment: 'fixed', fontFamily: 'Arial, sans-serif' },
    mainCard: { background: '#ffffff', borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', minWidth: 0 },
    title: { color: '#4a0028', fontWeight: '800', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' },
    titleIcon: { display: 'flex', alignItems: 'center' },

    // ═══ Hero ═══
    hero: { position: 'relative', overflow: 'hidden', color: 'white' },
    breadcrumb: { color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '12px' },
    heroTitle: { fontWeight: '700', margin: '0 0 16px', fontFamily: 'Cormorant Garamond, serif' },
    heroText: { color: 'rgba(255,255,255,0.78)', fontSize: '14px', lineHeight: '1.8', margin: 0 },

    // ═══ Barre supérieure ═══
    topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
    filterBtn: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 22px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '30px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', boxShadow: '0 4px 16px rgba(74,0,40,0.3)', flexShrink: 0, whiteSpace: 'nowrap' },
    fabBadge: { backgroundColor: '#c0306a', color: '#4a0028', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800', flexShrink: 0 },

    relationBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', background: 'rgba(60,60,80,0.05)', border: '1px solid #ecd9b9', borderRadius: '14px', padding: '16px 18px', marginBottom: '24px', color: '#7a1340', fontSize: '13.5px', lineHeight: '1.6', fontWeight: '600', flexWrap: 'wrap' },
    relationBannerText: { display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '220px' },
    profileSideId: { display: 'inline-flex', alignItems: 'center', gap: '5px', alignSelf: 'center', margin: '2px 0 8px', padding: '6px 12px', backgroundColor: '#fff', color: '#c0306a', border: '1.5px solid #f5d0e0', borderRadius: '30px', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' },

    endRelationBtn: { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 18px', backgroundColor: '#4a0028', color: '#ffffff', border: 'none', borderRadius: '9px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', flexShrink: 0, whiteSpace: 'nowrap' },

    // ═══ Modale (identique à NavbarAuth / Profile) ═══
    modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px', boxSizing: 'border-box' },
    modalBox: { background: '#ffffff', borderRadius: '18px', padding: '32px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', boxSizing: 'border-box', textAlign: 'center', border: '1px solid #ecd9b9' },
    modalIcon: { marginBottom: '12px', display: 'flex', justifyContent: 'center' },
    modalTitle: { color: '#4a0028', fontSize: '22px', fontWeight: '800', margin: '0 0 10px' },
    modalText: { color: '#9a7d88', fontSize: '14px', lineHeight: '1.6', margin: '0 0 26px' },
    modalActions: { display: 'flex', gap: '14px' },
    modalCancel: { flex: 1, textAlign: 'center', padding: '12px', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
    modalConfirm: { flex: 1, textAlign: 'center', padding: '12px', background: 'linear-gradient(135deg, #c0306a, #8b1a4a)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 14px rgba(192,48,106,0.35)' },

    // ═══ Modale "Voir le profil" — layout deux colonnes ═══
    profileModalBox: { background: '#faf7f2', borderRadius: '18px', padding: '28px', width: '100%', maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', boxSizing: 'border-box', textAlign: 'left', border: '1px solid #ecd9b9', position: 'relative' },
    profileModalClose: { position: 'absolute', top: '16px', right: '16px', background: 'rgba(139,26,74,0.08)', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 },

    profileLayout: { display: 'grid', gap: '22px', alignItems: 'start' },
    profileFooter: { gridColumn: '1 / -1', marginTop: '4px', paddingTop: '20px', borderTop: '1px solid #ecd9b9', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto', width: '100%' },

    // Colonne gauche : carte photo + actions
    profileSideCard: { background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(74,0,40,0.08)', border: '1px solid #ecd9b9' },
    profilePhotoBox: { width: '100%', height: '260px', backgroundColor: '#ffffff.', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    profileSideBody: { padding: '20px' },
    profileSideName: { color: '#8b1a4a', fontSize: '22px', fontWeight: '700', margin: '0 0 8px', fontFamily: 'Cormorant Garamond, serif' },
    profileSideLoc: { display: 'flex', alignItems: 'center', gap: '6px', color: '#9a7d88', fontSize: '13.5px', margin: '0 0 18px' },
    sideInterestBtn: { width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13.5px', boxShadow: '0 3px 12px rgba(74,0,40,0.25)' },
    sideInterestBtnActive: { width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: 'linear-gradient(135deg, #c0306a, #b8942f)', color: '#4a0028', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13.5px', boxShadow: '0 3px 12px rgba(60,60,80,0.06)' },
    sideFavoriBtn: { width: '100%', marginTop: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13.5px' },
    sideFavoriBtnActive: { width: '100%', marginTop: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: 'rgba(201,168,76,0.2)', color: '#4a0028', border: '1.5px solid #c0306a', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13.5px' },

    // Colonne droite : cartes d'informations
    profileMainCol: { display: 'flex', flexDirection: 'column', gap: '18px', minWidth: 0 },
    infoGrid: { display: 'grid', gap: '18px' },
    infoCard: { background: '#fff', borderRadius: '16px', padding: '20px 22px', boxShadow: '0 4px 20px rgba(74,0,40,0.06)', border: '1px solid #f0e4e9' },
    infoCardTitle: { display: 'flex', alignItems: 'center', gap: '8px', color: '#8b1a4a', fontSize: '15px', fontWeight: '700', margin: '0 0 14px', fontFamily: 'Cormorant Garamond, serif' },
    aboutText: { color: '#5a4a52', fontSize: '13.5px', lineHeight: '1.7', margin: 0 },
    infoLigne: { display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f0e4e9' },
    infoLabel: { color: '#9a7d88', fontSize: '13px', fontWeight: '500' },
    infoVal: { color: '#4a0028', fontSize: '13px', fontWeight: '600', textAlign: 'right' },

    sentInterestBox: { background: 'rgba(60,60,80,0.05)', border: '1px solid #ecd9b9', borderRadius: '16px', padding: '18px 22px' },
    sentInterestHead: { display: 'flex', alignItems: 'center', gap: '7px', color: '#7a1340', fontSize: '13px', fontWeight: '800', marginBottom: '10px' },
    editInterestBtn: { marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', backgroundColor: '#fff', color: '#8b1a4a', border: '1px solid #e6cfa8', borderRadius: '20px', cursor: 'pointer', fontWeight: '700', fontSize: '11.5px' },
    sentInterestText: { color: '#5a4a52', fontSize: '13.5px', lineHeight: '1.6', margin: 0, fontStyle: 'italic' },

    interestTextarea: { width: '100%', padding: '13px 15px', borderRadius: '10px', border: '1.5px solid #e6cfa8', fontSize: '14px', outline: 'none', resize: 'vertical', backgroundColor: '#fff', color: '#3a3a3a', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' },
    charCount: { textAlign: 'right', color: '#9a7d88', fontSize: '12px', margin: '6px 0 20px' },

    // ═══ Intérêts & Favoris (bouton topBar + drawer) ═══
    topBarBtns: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
    activiteBtn: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 22px', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '30px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', flexShrink: 0, whiteSpace: 'nowrap' },
    activiteBadge: { backgroundColor: '#c0306a', color: '#fff', borderRadius: '20px', minWidth: '22px', height: '22px', padding: '0 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800', flexShrink: 0 },
    activiteRetirerBtn: { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 12px', backgroundColor: '#fff', color: '#9a7d88', border: '1.5px solid #e6cfa8', borderRadius: '30px', cursor: 'pointer', fontWeight: '700', fontSize: '11.5px', whiteSpace: 'nowrap' },
    activiteTabs: { display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' },
    activiteTab: { flex: 1, minWidth: '140px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '10px 14px', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '30px', cursor: 'pointer', fontWeight: '700', fontSize: '12.5px' },
    activiteTabActive: { flex: 1, minWidth: '140px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '10px 14px', background: '#c0306a', color: '#fff', border: '1.5px solid #4a0028', borderRadius: '30px', cursor: 'pointer', fontWeight: '700', fontSize: '12.5px', boxShadow: '0 3px 12px rgba(74,0,40,0.25)' },
    activiteList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    activiteItem: { background: 'rgba(60,60,80,0.04)', border: '1px solid #ecd9b9', borderRadius: '14px', padding: '14px 16px' },
    activiteTop: { display: 'flex', alignItems: 'center', gap: '12px' },
    activiteAvatar: { width: '44px', height: '44px', borderRadius: '50%', overflow: 'hidden', border: '2px solid #c0306a', backgroundColor: '#4a0028', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    activiteName: { display: 'flex', alignItems: 'center', gap: '6px', color: '#4a0028', fontSize: '14.5px', fontWeight: '800' },
    activiteMeta: { color: '#9a7d88', fontSize: '12.5px', fontWeight: '600' },
    activiteMessage: { color: '#5a4a52', fontSize: '13px', lineHeight: '1.6', margin: '12px 0 0', fontStyle: 'italic', padding: '10px 12px', background: '#fff', borderRadius: '10px', border: '1px solid #ecd9b9' },
    activiteFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginTop: '12px', flexWrap: 'wrap' },
    activiteDate: { color: '#9a7d88', fontSize: '11.5px', fontWeight: '600' },
    activiteViewBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '30px', cursor: 'pointer', fontWeight: '700', fontSize: '12px', flexShrink: 0 },
    activiteRemoveBtn: { width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#fff', border: '1.5px solid #e6cfa8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    activiteEmpty: { textAlign: 'center', color: '#9a7d88', fontSize: '13.5px', fontStyle: 'italic', padding: '40px 20px', lineHeight: '1.6' },

    // ═══ Cartes membres ═══
    grid: { display: 'grid', gap: '16px' },
    card: { background: '#fff', borderRadius: '16px', padding: '22px 16px', textAlign: 'center', boxShadow: '0 4px 16px rgba(74,0,40,0.08)', border: '1px solid #ecd9b9' },
    avatarWrap: { position: 'relative', width: '90px', height: '90px', margin: '0 auto 14px' },
    avatarImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' },
    avatarPlaceholder: { width: '100%', height: '100%', borderRadius: '50%', backgroundColor: '#4a0028', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid #c0306a' },
    genderDot: { position: 'absolute', bottom: '2px', right: '2px', width: '26px', height: '26px', borderRadius: '50%', backgroundColor: '#c0306a', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' },
    cardName: { color: '#4a0028', fontSize: '16px', fontWeight: '800', margin: '0 0 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
    cardSub: { color: '#9a7d88', fontSize: '13px', margin: '0 0 8px' },
    cardStatus: { color: '#9a7d88', fontSize: '12px', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' },
    cardBtn: { width: '100%', padding: '10px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', boxShadow: '0 3px 12px rgba(74,0,40,0.25)' },
    pendingBtn: { background: '#b9a3ae', cursor: 'not-allowed', boxShadow: 'none' },
    acceptBtn: { background: 'linear-gradient(135deg, #2c5f2e, #3d8b40)' },
    rejectBtn: { background: 'linear-gradient(135deg, #8b1a1a, #c00)' },
    rejectedBtn: { background: 'rgba(60,60,80,0.05)', color: '#9a7d88', cursor: 'not-allowed', boxShadow: 'none' },
    occupeBtn: { background: 'rgba(154,125,136,0.12)', color: '#9a7d88', cursor: 'not-allowed', boxShadow: 'none', border: '1.5px dashed #d4c0c8' },
    expiredBtn: {
        background: '#c0306a', color: 'white'
    },
    viewProfileBtn: { width: '100%', marginTop: '8px', padding: '9px', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },

    // ═══ Actions sur carte quand la fiche est bloquée ═══
    cardActionsRow: { display: 'flex', gap: '6px', marginTop: '8px' },
    cardMiniBtn: { width: '38px', padding: '9px 0', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    cardMiniBtnActive: { width: '38px', padding: '9px 0', background: 'rgba(60,60,80,0.06))', color: '#4a0028', border: '1.5px solid #c0306a', borderRadius: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    cardLockBtn: { flex: 1, padding: '9px', background: 'rgba(201,168,76,0.1)', color: '#9a7d88', border: '1.5px dashed #e6cfa8', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '11.5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px' },

    // ═══ Bandeau invitation abonnement ═══
    aboBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', background: 'rgba(60,60,80,0.05)', border: '1px solid #ecd9b9', borderRadius: '14px', padding: '16px 18px', marginBottom: '24px', color: '#7a1340', fontSize: '13.5px', lineHeight: '1.6', fontWeight: '600', flexWrap: 'wrap' },
    aboBannerText: { display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '220px' },
    aboBannerBtn: { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 18px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '30px', cursor: 'pointer', fontWeight: '800', fontSize: '12px', flexShrink: 0, whiteSpace: 'nowrap' },

    pagination: { display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '28px', flexWrap: 'wrap' },
    pageBtn: { width: '38px', height: '38px', borderRadius: '10px', border: '1.5px solid #e6cfa8', backgroundColor: '#fff', color: '#8b1a4a', cursor: 'pointer', fontWeight: '700' },
    pageActive: { width: '38px', height: '38px', borderRadius: '10px', border: 'none', background: '#c0306a', color: '#f3e0c7', cursor: 'pointer', fontWeight: '800', boxShadow: '0 4px 14px rgba(74,0,40,0.3)' },

    loading: { textAlign: 'center', color: '#4a0028', fontSize: '16px', fontWeight: '600', padding: '40px 0' },
    empty: { textAlign: 'center', color: '#9a7d88', fontSize: '15px', fontStyle: 'italic', padding: '40px 0' },

    // ═══ Champs (identiques à Profile) ═══
    label: { color: '#7a1340', fontWeight: '700', marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' },
    labelIcon: { display: 'flex', alignItems: 'center' },
    input: { flex: 1, padding: '11px 14px', border: '1.5px solid #e6cfa8', borderRadius: '10px', fontSize: '14px', outline: 'none', color: '#3a3a3a', backgroundColor: '#fff', minWidth: 0, boxSizing: 'border-box' },
    select: { width: '100%', padding: '11px 14px', border: '1.5px solid #e6cfa8', borderRadius: '10px', fontSize: '14px', outline: 'none', color: '#3a3a3a', backgroundColor: '#fff', cursor: 'pointer', boxSizing: 'border-box' },
    rangeRow: { display: 'flex', gap: '8px', alignItems: 'center' },
    rangeSep: { color: '#c0a8b4', flexShrink: 0 },
    searchBtn: { width: '44px', flexShrink: 0, background: '#c0306a', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 12px rgba(74,0,40,0.25)' },

    applyBtn: { width: '100%', marginTop: '20px', padding: '13px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 16px rgba(74,0,40,0.3)' },
    resetBtn: { width: '100%', marginTop: '10px', padding: '13px', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },

    onlineAvatar: { width: '44px', height: '44px', borderRadius: '50%', overflow: 'hidden', border: '2px solid #c0306a', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#4a0028', flexShrink: 0 },

    // ═══ Overlay & Drawer ═══
    overlay: { position: 'fixed', inset: 0, zIndex: 100, backgroundColor: 'rgba(0,0,0,0.6)', transition: 'opacity 0.3s ease, visibility 0.3s ease' },
    drawer: { position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 101, background: '#ffffff', boxShadow: '-4px 0 30px rgba(0,0,0,0.3)', transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)', display: 'flex', flexDirection: 'column', maxWidth: '92vw' },
    drawerHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px', background: '#c0306a', flexShrink: 0 },
    drawerTitle: { color: '#f3e0c7', fontSize: '17px', fontWeight: '800', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' },
    closeBtn: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(243,224,199,0.3)', width: '34px', height: '34px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    drawerBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    sideCard: { background: 'rgba(60,60,80,0.04)', borderRadius: '14px', padding: '20px', border: '1px solid #ecd9b9', marginBottom: '16px' },
    sideTitle: { color: '#4a0028', fontSize: '16px', fontWeight: '800', margin: '0 0 14px' },
    sideText: { color: '#9a7d88', fontSize: '13px', margin: '0 0 12px' },
};

export default Dashboard;